import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import { analyticsControllerGetTenantAppDownloadsChart } from '@/generated/korza-api-client';
import type {
  TenantAppDownloadsChartResponseDto,
  TenantHomeResponseDto,
} from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';
import { tenantHomeQueryOptions } from '@/hooks/use-tenant-home';
import type { PredefinedRange } from '@/hooks/use-metrics-date-filter';
import { useWithPrevious } from '@/hooks/use-with-previous';
import type { ChartGranularity } from '@/lib/chart-axis';
import { bucketSumChartData } from '@/lib/hooks/use-chart-data';
import { computeAverageTicket, toHomeChartRows } from '@/lib/home-metrics';
import { mobileLayer } from '@/lib/mobile-layer';
import {
  comparisonLabel,
  computeDelta,
  type DeltaView,
} from '@/lib/period-comparison';
import { formatCurrency } from '@/components/metrics/formatters';
import { ComparisonLabel, DeltaBadge } from '@/components/metrics/delta-badge';
import {
  HomeSparkline,
  type SparklineRow,
} from '@/components/home/home-sparkline';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

export interface HomeKpiTilesProps {
  /** The period of the `PeriodFilter`; `preset` undefined = custom. */
  range: { from: string; to: string; preset?: PredefinedRange };
  granularity: ChartGranularity;
}

type TileKey = 'revenue' | 'orders' | 'averageTicket' | 'downloads';

interface TileContent {
  value: string;
  delta: DeltaView;
  spark: SparklineRow[];
}

/** Same key as the Analytics downloads chart, so the cache is shared. */
function useDownloadsChart(
  tenantSlug: string,
  from: string,
  to: string,
  granularity: ChartGranularity
) {
  return useQuery<TenantAppDownloadsChartResponseDto, Error>({
    queryKey: ['appDownloadsChart', tenantSlug, from, to, granularity],
    queryFn: async () => {
      const res = await analyticsControllerGetTenantAppDownloadsChart({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to, granularity },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

/**
 * Variation row of a tile (HOME-02): the badge line (18px), then the "vs. …"
 * label on its own lines. The label depends only on the period, so the
 * skeleton shows it too and both states wrap it the same way at any width;
 * a sizer in the same grid cell keeps that height when the loaded tile has
 * no badge ("sem dados…" or no variation). Its label is a pseudo-element, so
 * the text is not in the DOM twice.
 */
function DeltaRow({
  comparison,
  children,
}: {
  comparison: string;
  children: ReactNode;
}) {
  return (
    <div className="grid items-start [&>*]:[grid-area:1/1]">
      <span aria-hidden className="invisible flex flex-col gap-1">
        <span className="h-[18px]" />
        <span
          data-label={comparison}
          className="text-xs before:content-[attr(data-label)]"
        />
      </span>
      {children}
    </div>
  );
}

const ticketOf = (data: TenantHomeResponseDto) =>
  computeAverageTicket(data.totalRevenue, data.totalPaidOrders);

/**
 * The 4 indicators of the Início (HOME-02): Faturamento, Vendas, Ticket
 * médio and Downloads of the period, each with its variation against the
 * previous period (CMP-01…03) and a minigráfico with the partial bucket.
 * 4 columns from 1180px, 2×2 below.
 */
export function HomeKpiTiles({ range, granularity }: HomeKpiTilesProps) {
  const { LL, locale } = useI18nContext();
  const tenant = useTenant();
  const home = useWithPrevious(
    (r) =>
      tenantHomeQueryOptions(tenant.slug, {
        fromDate: r.from,
        toDate: r.to,
        granularity,
      }),
    range
  );
  const downloadsChart = useDownloadsChart(
    tenant.slug,
    range.from,
    range.to,
    granularity
  );
  const current = home.current.data;
  const previous = home.previous.data;

  const homeRows = useMemo(
    () =>
      current
        ? toHomeChartRows(current.revenueChart.points, granularity, locale)
        : [],
    [current, granularity, locale]
  );
  const downloadRows = useMemo<SparklineRow[]>(
    () =>
      bucketSumChartData(
        downloadsChart.data?.points ?? [],
        granularity,
        locale,
        (point) => point.downloadsCount,
        'value'
      ).map((row) => ({
        month: row.month,
        date: String(row.date),
        value: Number(row.value),
      })),
    [downloadsChart.data, granularity, locale]
  );

  if (home.current.isError) {
    return (
      <Card className="flex flex-col items-start gap-3 p-6 max-md:p-4">
        <p className="text-sm text-muted-foreground">
          {LL.home.kpis.loadError()}
        </p>
        <Button
          type="button"
          variant="outline"
          className={mobileLayer.touch}
          onClick={() => void home.current.refetch()}
        >
          {LL.home.kpis.retry()}
        </Button>
      </Card>
    );
  }

  const spark = (valueOf: (row: (typeof homeRows)[number]) => number) =>
    homeRows.map((row) => ({
      month: row.month,
      date: row.date,
      value: valueOf(row),
    }));

  const contentOf = (
    data: TenantHomeResponseDto
  ): Record<TileKey, TileContent> => {
    const ticket = ticketOf(data);
    return {
      revenue: {
        value: formatCurrency(data.totalRevenue, locale, data.currency),
        delta: home.delta((d) => d.totalRevenue),
        spark: spark((row) => row.revenue),
      },
      orders: {
        value: String(data.totalPaidOrders),
        delta: home.delta((d) => d.totalPaidOrders),
        spark: spark((row) => row.count),
      },
      averageTicket: {
        value:
          ticket === null ? '—' : formatCurrency(ticket, locale, data.currency),
        // No sales now → no variation; none before → "sem dados…" (CMP-02).
        delta:
          ticket === null || home.previous.isError || !previous
            ? { kind: 'none' }
            : computeDelta(ticket, ticketOf(previous) ?? 0, locale),
        // A bucket without sales reads 0, like `aovApp` of /charts/aov.
        spark: spark((row) => (row.count ? row.revenue / row.count : 0)),
      },
      downloads: {
        value: String(data.downloads),
        delta: home.delta((d) => d.downloads),
        spark: downloadRows,
      },
    };
  };
  const content = current ? contentOf(current) : null;

  const tiles: Array<{ key: TileKey; label: string }> = [
    { key: 'revenue', label: LL.home.kpis.revenue() },
    { key: 'orders', label: LL.home.kpis.orders() },
    { key: 'averageTicket', label: LL.home.kpis.averageTicket() },
    { key: 'downloads', label: LL.home.kpis.downloads() },
  ];
  const now = DateTime.now();
  const comparison = comparisonLabel(range.preset, LL, now);

  return (
    <div
      data-home-kpis
      className="grid grid-cols-2 gap-3 min-[1180px]:grid-cols-4"
    >
      {tiles.map(({ key, label }) => {
        const tile = content?.[key];
        return (
          <Card
            key={key}
            data-kpi={key}
            className="flex min-w-0 flex-col gap-1.5 p-4 max-md:p-3"
          >
            <p className="text-sm font-medium text-muted-foreground max-md:text-xs">
              {label}
            </p>
            {tile ? (
              <>
                <p
                  data-kpi-value
                  className="truncate text-2xl font-semibold tabular-nums tracking-[-0.02em] max-md:text-lg"
                >
                  {tile.value}
                </p>
                <DeltaRow comparison={comparison}>
                  <DeltaBadge
                    className="gap-y-1"
                    delta={tile.delta}
                    comparison={
                      <ComparisonLabel
                        className="basis-full"
                        preset={range.preset}
                        now={now}
                      />
                    }
                  />
                </DeltaRow>
                <HomeSparkline
                  rows={tile.spark}
                  range={range}
                  granularity={granularity}
                />
              </>
            ) : (
              <>
                <Skeleton className="h-8 w-24 max-md:h-7" />
                <DeltaRow comparison={comparison}>
                  <span className="flex flex-col items-start gap-1">
                    <Skeleton className="h-[18px] w-16" />
                    <ComparisonLabel preset={range.preset} now={now} />
                  </span>
                </DeltaRow>
                <Skeleton className="h-8 w-full" />
              </>
            )}
          </Card>
        );
      })}
    </div>
  );
}
